export { useApi } from "Services/AbstractApiService";
import { apiHeaders, configureServer } from "Services/AbstractApiService";
import { getApiUrl } from "Services/ProxyConfig";
import { fetch } from "Services/util/fetchPonyfill";
import { MessagePreview } from "Types/Campaign";
import { KnowledgeBaseSourceFileObjectLocal } from "Types/KnowledgeBase";

import { brandSlugFromAuth } from "./authState";

import type { UpdateFive9AiAgentConnectSettingsRequest } from "@regal-voice/regal-client";
import type { RcFile } from "antd/es/upload";
import type { BrandIntegration } from "Types/Integrations";
export const makeRequest = configureServer({ host: getApiUrl() });

export async function getGoogleSsoRequired(email: string): Promise<{ required: boolean; redirect: string }> {
    const headers = apiHeaders();
    const result = await makeRequest<{ required: boolean; redirect?: string }>({
        headers,
        endpoint: `/authenticate/google-sso-required?email=${encodeURIComponent(email)}&validateDomain=true`,
        errors: {
            notFound: "Route not found",
            other: "Error fetching results",
        },
    });

    return {
        required: result.required,
        redirect: result.redirect || "either",
    };
}

export function triggerCampaignTest(
    body: MessagePreview,
    type: "SMS" | "Phone Call"
): Promise<{ result: string; resultMediaUrl?: string; error: string }> {
    return makeRequest<any>({
        endpoint: `/interactions`,
        errors: {
            notFound: "Contact not found",
            other: `Error triggering preview ${type}`,
        },
        body,
    });
}

export function uploadSegmentCSV(
    file: RcFile,
    segment_uuid: string,
    includeCsvDataTypeRow: boolean
): Promise<{ path: string; id: string; url: string }> {
    const formData = new FormData();
    formData.set("includeCsvDataTypeRow", includeCsvDataTypeRow?.toString() || "false");
    formData.append("file", file);
    const params = {
        method: "POST",
        headers: apiHeaders(),
        body: formData,
    };
    return fetch(`${getApiUrl()}/segments/${segment_uuid}/uploadFile`, params).then((response: Response) => {
        if (response.status == 404) {
            throw "Route not found";
        } else if (response.status === 400) {
            return response.json().then((e) => {
                throw e;
            });
        } else if (!response.ok) {
            throw "Error uploading file";
        }
        return response.json();
    });
}

export async function uploadKnowledgeBaseSourceItem(
    sourceItem: KnowledgeBaseSourceFileObjectLocal
): Promise<{ success: boolean; path: string; mimeType: string; id: string; fileName: string }> {
    const formData = new FormData();
    formData.append("files", sourceItem.file);
    const brandSlug = brandSlugFromAuth();

    const params = {
        method: "POST",
        headers: apiHeaders(),
        body: formData,
    };
    const response = await fetch(`${getApiUrl()}/ai-knowledge-base/stream-upload-files?b=${brandSlug}`, params);
    if (response.status == 404) {
        throw "Route not found";
    } else if (response.status === 400) {
        return response.json().then((e) => {
            throw e;
        });
    } else if (!response.ok) {
        throw "Error uploading source item";
    }
    return await response.json();
}

export function fetchSourceItemPreviewUrl({
    id,
    filename,
}: {
    id: string;
    filename: string;
}): Promise<{ url: string }> {
    return makeRequest<{ url: string }>({
        endpoint: `/ai-knowledge-base/preview/${id}/${filename}`,
        errors: {
            notFound: "Route not found",
            other: "Failed to generate preview url",
        },
    });
}

export function getKnowledgeBaseSourceItemDownloadUrl(id: string, filename: string) {
    return `${getApiUrl()}/ai-knowledge-base/download/${id}/${filename}`;
}

export function authenticateHubspot(code: string): Promise<any> {
    return makeRequest({
        endpoint: `/hubspot/oauth-callback/${code}`,
        errors: {
            notFound: "Route not found",
            other: "Error fetching results",
        },
    });
}

export function connectCalendly(calendlyKey: string): Promise<any> {
    return makeRequest({
        endpoint: "/calendly/connect",
        body: {
            calendlyKey,
        },
        errors: {
            notFound: "Calendly Connect route not found",
            other: "Error configuring calendly integration while connecting",
        },
    });
}

export function disconnectCalendly(): Promise<any> {
    return makeRequest({
        endpoint: "/calendly/disconnect",
        method: "POST",
        errors: {
            notFound: "Calendly Disconnect route not found",
            other: "Error configuring calendly integration while disconnecting",
        },
    });
}

export function authorizeSalesforce(
    code: string,
    redirectUri: string,
    authorizationHost: string,
    codeVerifier: string
): Promise<any> {
    return makeRequest({
        endpoint: `/salesforce/authorize/`,
        body: {
            code,
            redirectUri,
            authorizationHost,
            codeVerifier,
        },
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error fetching results",
        },
    });
}

export function disconnectSalesforce(): Promise<any> {
    return makeRequest({
        endpoint: `/salesforce/disconnect/`,
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error with Salesforce integration while disconnecting",
        },
    });
}

export function connectOutboundAni(apiKey: string): Promise<any> {
    return makeRequest({
        endpoint: "/outbound-ani/connect",
        method: "POST",
        body: {
            apiKey,
        },
        errors: {
            notFound: "Route not found",
            other: "Error connecting outboundANI",
        },
    });
}

export function disconnectOutboundAni(): Promise<any> {
    return makeRequest({
        endpoint: "/outbound-ani/disconnect",
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error disconnecting outboundANI",
        },
    });
}

export function connectFive9(username: string, password: string): Promise<any> {
    return makeRequest({
        endpoint: "/five9/connect",
        method: "POST",
        body: {
            username,
            password,
        },
        errors: {
            notFound: "Route not found",
            other: "Error connecting Five9",
        },
    });
}

export function disconnectFive9(): Promise<any> {
    return makeRequest({
        endpoint: "/five9/disconnect",
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error disconnecting Five9",
        },
    });
}

export function updateFive9AiAgentConnectSettings(
    body: UpdateFive9AiAgentConnectSettingsRequest
): Promise<BrandIntegration> {
    return makeRequest({
        endpoint: "/five9/ai-agent-connect-settings",
        method: "PATCH",
        body,
        errors: {
            notFound: "Route not found",
            other: "Error updating Five9 AI Agent Connect settings",
        },
    });
}

export function connectGryphon(
    licenseID: string,
    enabledTaskTitles: Record<string, string>,
    isActive?: boolean
): Promise<any> {
    return makeRequest({
        endpoint: "/gryphon/connect",
        method: "POST",
        body: {
            licenseID,
            enabledTaskTitles,
            ...(typeof isActive === "boolean" ? { isActive } : {}),
        },
        errors: {
            notFound: "Route not found",
            other: "Error connecting Gryphon",
        },
    });
}

export function disconnectGryphon(): Promise<any> {
    return makeRequest({
        endpoint: "/gryphon/disconnect",
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error disconnecting Gryphon",
        },
    });
}

export function deleteGryphon(): Promise<any> {
    return makeRequest({
        endpoint: "/gryphon/delete",
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error deleting Gryphon configuration",
        },
    });
}

export function getCampaignsCountUsingFive9QueuesForTransfer(): Promise<{ campaignsCount: number }> {
    return makeRequest({
        endpoint: "/five9/count-campaigns-using-queues-for-transfer",
        errors: {
            notFound: "Route not found",
            other: "Error fetching campaigns using Five9 queues for transfer",
        },
    });
}

export async function syncFive9Queues(): Promise<{ success: boolean }> {
    const result = await makeRequest<{ found: number }>({
        endpoint: "/five9/sync-queues",
        method: "POST",
        errors: {
            notFound: "Route not found",
            other: "Error syncing Five9 queues",
        },
    });

    if (!result.found) {
        throw "No queues found to sync";
    }

    return { success: true };
}
